import { Injectable } from "@angular/core";
import { CHttp } from "../../core/services/chttp.service";
import { config } from "../../app.config";
import { Observable } from "rxjs";
import { MemberEvent, JobTitle, ManagementLevel, AreaOfBusiness, Employer, MemberDevice } from "../members.interfaces";
import { Response } from "@angular/http";
import { EmailCategory, EmailResult, MemberEmail, parseMemberEmails, memberEmailError } from '../member-emails';
import 'rxjs/add/observable/of';

@Injectable()
export class MemberService {
  constructor(private http: CHttp) { }

  public getMembers(data?: any) {
    return this.http.get(config.api_url + "members", {
      params: data,
    });
  }

  public getForVerification(data?: any) {
    return this.http.get(config.api_url + "members/for-verification", {
      params: data,
    });
  }

  public getVerificationCodes(data?: any) {
    return this.http.get(config.api_url + "members/verification-codes");
  }

  public getMember(id: string) {
    return this.http.get(config.api_url + "members/" + id);
  }

  public getMemberWebProfile(id: string) {
    return this.http.get(config.api_url + "members/" + id + "/web-profile");
  }

  public getMemberDevices(id: string): Observable<MemberDevice[]> {
    return this.http.get(config.api_url + "members/" + id + "/devices").map(resp => resp.json());
  }

  /** Reconcile saved email values from the member profile after a mutation. */
  public refreshMemberEmails(id: string): Observable<EmailResult<MemberEmail[]>> {
    return this.getMember(id)
      .map(response => parseMemberEmails(response.json().emailAddresses))
      .catch(error => Observable.of(memberEmailError(error)));
  }

  /** Add an unverified secondary or replace an existing address. */
  public addEmail(id: string, email: string, category: EmailCategory, replacesId: string | null): Observable<EmailResult<void>> {
    return this.emailRequest(this.http.post(`${config.api_url}members/${id}/emails`, { email, category, replacesId }));
  }

  /** Save the member-selected category independently of delivery. */
  public setEmailCategory(id: string, addressId: string, category: EmailCategory): Observable<EmailResult<void>> {
    return this.emailRequest(this.http.put(`${config.api_url}members/${id}/emails/${addressId}/category`, { category }));
  }

  /** Switch ordinary communications to a verified secondary address. */
  public makePrimaryEmail(id: string, addressId: string): Observable<EmailResult<void>> {
    return this.emailRequest(this.http.put(`${config.api_url}members/${id}/emails/${addressId}/primary`));
  }

  /** Send a new verification code to the unverified inbox. */
  public resendEmailVerification(id: string, addressId: string): Observable<EmailResult<void>> {
    return this.emailRequest(this.http.post(`${config.api_url}members/${id}/emails/${addressId}/resend`));
  }

  /** Remove a secondary address or cancel a pending replacement. */
  public removeEmail(id: string, addressId: string): Observable<EmailResult<void>> {
    return this.emailRequest(this.http.delete(`${config.api_url}members/${id}/emails/${addressId}`));
  }

  private emailRequest(request: Observable<Response>): Observable<EmailResult<void>> {
    return request.map((): EmailResult<void> => ({ ok: true, value: undefined }))
      .catch(error => Observable.of(memberEmailError(error)));
  }

  public updateMember(data: any, id: string) {
    return this.http.put(`${config.api_url}members/${id}`, data);
  }

  public updateVipStatus(id: string, isVip: boolean) {
    return this.http.put(`${config.api_url}members/${id}/update-vip`, {
      isVip,
    });
  }

  public deleteMember(id: string, isGdpr: boolean) {
    return this.http.delete(`${config.api_url}members/${id}?isGdpr=${isGdpr}`);
  }

  public acceptMember(id: string) {
    return this.http.post(`${config.api_url}members/${id}/approve`);
  }

  public bulkAcceptMembers(userIds: string[]) {
    return this.http.post(`${config.api_url}members/approve/all`, { userIds });
  }

  public rejectMember(id: string) {
    return this.http.post(`${config.api_url}members/${id}/reject`);
  }

  public bulkRejectMembers(userIds: string[]) {
    return this.http.post(`${config.api_url}members/reject/all`, { userIds });
  }

  public exportUrl(query: string) {
    return this.http.get(config.api_url + "members/report" + query);
  }

  public requestNewId(id: string, type: string) {
    const body = { type };

    return this.http.post(
      `${config.api_url}members/${id}/request-new-proof-of-employment`,
      body,
    );
  }

  public bulkRequestNewIds(userIds: string[], type: string) {
    const body = {
      reason: { type },
      users: { userIds },
    };

    return this.http.post(
      `${config.api_url}members/request-new-proof-of-employment/all`,
      body,
    );
  }

  public suspendMember(id: string) {
    return this.http.post(`${config.api_url}members/${id}/suspend`);
  }

  public unsuspendMember(id: string) {
    return this.http.post(`${config.api_url}members/${id}/unsuspend`);
  }

  public forgottenPassword(email: string) {
    return this.http.post(`${config.api_url}members/forgotten-password`, {
      email: email,
    });
  }

  public cancelSubscriptionEndOfPeriod(userId: string) {
    return this.http.post(
      `${config.api_url}members/subscriptions/${userId}/cancel-end-of-period`,
    );
  }

  public cancelSubscriptionNow(userId: string) {
    return this.http.post(
      `${config.api_url}members/subscriptions/${userId}/cancel-now`,
    );
  }

  public bulkCancelSubscriptionsEndOfPeriod(userIds: string[]) {
    return this.http.post(
      `${config.api_url}members/subscriptions/cancel-end-of-period`,
      { userIds },
    );
  }

  public bulkCancelSubscriptionsNow(userIds: string[]) {
    return this.http.post(`${config.api_url}members/subscriptions/cancel-now`, {
      userIds,
    });
  }

  public bulkRemoveVIP(userIds: string[]) {
    return this.http.post(`${config.api_url}members/subscriptions/remove-vip`, {
      userIds,
    });
  }

  public addSubscription(id: string, data: any) {
    return this.http.post(
      `${config.api_url}members/subscriptions/${id}/system`,
      data,
    );
  }

  public getMemberStatuses() {
    return [
      {
        name: "All Statuses",
        value: "",
      },
      {
        name: "Waiting for verification",
        value: "WAITING_FOR_VERIFICATION",
      },
      {
        name: "Waiting for profile",
        value: "WAITING_FOR_PROFILE",
      },
      {
        name: "Waiting for payment",
        value: "WAITING_FOR_PAYMENT",
      },
      {
        name: "Waiting for proof",
        value: "WAITING_FOR_PROOF",
      },
      {
        name: "Waiting for Approval",
        value: "WAITING_FOR_APPROVAL",
      },
      {
        name: "Free Trial",
        value: "FREE_TRIAL",
      },
      {
        name: "Free Trial Ended",
        value: "FREE_TRIAL_ENDED",
      },
      {
        name: "Paid Member",
        value: "PAID_MEMBER",
      },
      {
        name: "Non-renewal",
        value: "NON_RENEWAL",
      },
      {
        name: "Free",
        value: "FREE",
      },
      {
        name: "VIP",
        value: "VIP",
      },
      {
        name: "Failed payment",
        value: "FAILED_PAYMENT",
      },
      {
        name: "Rejected",
        value: "REJECTED",
      },
      {
        name: "Suspended",
        value: "SUSPENDED",
      },
    ];
  }

  public getMemberRegistrationStatuses() {
    return [
      {
        name: "Waiting for validation",
        value: "WAITING_FOR_VALIDATION",
      },
      {
        name: "Waiting for personal information",
        value: "WAITING_FOR_PERSONAL_INFORMATION",
      },
      {
        name: "Waiting for payment information",
        value: "WAITING_FOR_PAYMENT_DETAILS",
      },
      {
        name: "Waiting for proof",
        value: "WAITING_FOR_PROOF_OF_EMPLOYMENT",
      },
    ];
  }

  public getMemberEvents(id: string): Observable<any> {
    return this.http.get(`${config.api_url}members/${id}/events`);
  }

  public getJobTitles(): Observable<JobTitle[]> {
    return this.http.get(config.api_url + "job-titles/active").map(resp => resp.json());
  }

  public getManagementLevels(): Observable<ManagementLevel[]> {
    return this.http.get(config.api_url + "management-levels").map(resp => resp.json());
  }

  public getAreasOfBusiness(): Observable<AreaOfBusiness[]> {
    return this.http.get(config.api_url + "areas-of-business").map(resp => resp.json());
  }

  public getEmployers(): Observable<Employer[]> {
    return this.http.get(config.api_url + "employers/active").map(resp => resp.json());
  }
}



// WEBPACK FOOTER //
// /home/runner/_work/monorepo/monorepo/admin-portal-old/src/app/member/services/member.service.ts